<script setup>
import { ref, onMounted } from 'vue';
import Editor from '@tinymce/tinymce-vue';

// Editor-Inhalt
const privacyContent = ref('');
const loading = ref(false);
const error = ref(null);
const success = ref(null);

// TinyMCE-API-Key
const TINYMCE_API_KEY = '1t9kxhlnvxnradg7zg6anbasvijrjtrr2k12wr0aqab5av8y';

// Datenschutz-Daten laden
const loadPrivacyContent = async () => {
  try {
    loading.value = true;
    const response = await fetch('/api/privacy.php');
    const data = await response.json();
    
    if (data.success && data.privacy) {
      privacyContent.value = data.privacy;
    } else {
      error.value = data.message || 'Fehler beim Laden der Datenschutzerklärung';
    }
  } catch (err) {
    console.error('Fehler beim Laden der Datenschutzerklärung:', err);
    error.value = 'Die Datenschutzerklärung konnte nicht geladen werden. Bitte versuchen Sie es später erneut.';
  } finally {
    loading.value = false;
  }
};

// Datenschutzerklärung speichern
const savePrivacyContent = async () => {
  try {
    loading.value = true;
    error.value = null;
    success.value = null;
    
    const response = await fetch('/api/privacy.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      body: JSON.stringify({
        content: privacyContent.value
      })
    });
    
    const data = await response.json();
    
    if (data.success) {
      success.value = 'Datenschutzerklärung wurde erfolgreich gespeichert!';
      
      // Erfolgsmeldung nach 3 Sekunden ausblenden
      setTimeout(() => {
        success.value = null;
      }, 3000);
    } else {
      error.value = data.message || 'Fehler beim Speichern der Datenschutzerklärung';
    }
  } catch (err) {
    console.error('Fehler beim Speichern der Datenschutzerklärung:', err);
    error.value = 'Die Datenschutzerklärung konnte nicht gespeichert werden. Bitte versuchen Sie es später erneut.';
  } finally {
    loading.value = false;
  }
};

// TinyMCE-Editor Konfiguration
const editorConfig = {
  plugins: 'lists link table code help wordcount',
  toolbar: 'undo redo | formatselect | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link table | code | help',
  menubar: 'file edit view insert format tools table help',
  height: 500,
  language: 'de',
  language_url: 'https://cdn.jsdelivr.net/npm/tinymce-i18n@23.7.24/langs5/de.min.js',
  skin: 'oxide',
  branding: false,
  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
};

// Bei Komponentenaufbau Daten laden
onMounted(() => {
  loadPrivacyContent();
});
</script>

<template>
  <div class="privacy-manager">
    <div class="section-header">
      <h2>Datenschutzerklärung bearbeiten</h2>
      <p class="section-description">
        Hier können Sie die Datenschutzerklärung Ihrer Website bearbeiten.
      </p>
    </div>
    
    <div class="editor-container">
      <!-- Lade-Indikator -->
      <div v-if="loading" class="loading-overlay">
        <div class="spinner"></div>
        <span>Wird geladen...</span>
      </div>
      
      <!-- Fehlermeldung -->
      <div v-if="error" class="error-message">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>{{ error }}</span>
      </div>
      
      <!-- Erfolgsmeldung -->
      <div v-if="success" class="success-message">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        <span>{{ success }}</span>
      </div>
      
      <!-- TinyMCE-Editor -->
      <Editor
        :api-key="TINYMCE_API_KEY"
        v-model="privacyContent"
        :init="editorConfig"
        cloudChannel="6" 
      />
      
      <div class="form-actions">
        <button @click.prevent="savePrivacyContent" class="btn-save" :disabled="loading">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
          Datenschutzerklärung speichern
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.privacy-manager {
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 0;
}

.section-header {
  margin-bottom: 2rem;
}

.section-header h2 {
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.section-description {
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.5;
}

.editor-container {
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  position: relative;
}

/* Für den Editor platzhalten, wird von TinyMCE ersetzt */
#privacy-editor {
  display: block;
  width: 100%;
  min-height: 500px;
  border: 1px solid #ddd;
  margin-bottom: 1.5rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  border-radius: 4px;
  border: none;
  background-color: var(--primary-color);
  color: white;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-normal);
}

.btn-save:hover {
  background-color: var(--primary-color-dark);
}

.btn-save:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s infinite linear;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message, .success-message {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 4px;
}

.error-message {
  background-color: rgba(244, 67, 54, 0.1);
  color: #d32f2f;
}

.success-message {
  background-color: rgba(76, 175, 80, 0.1);
  color: #388e3c;
}
</style> 